import { useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v));
}

/**
 * Returns the nearest ancestor (including `start`) that can scroll vertically,
 * or `null` when the document viewport (`window`) is the effective scroll root.
 */
export function getNearestVerticalScrollContainer(
    start: HTMLElement
): HTMLElement | null {
    let el: HTMLElement | null = start;
    while (el) {
        const { overflowY } = getComputedStyle(el);
        const canScrollY =
            (overflowY === "auto" ||
                overflowY === "scroll" ||
                overflowY === "overlay") &&
            el.scrollHeight > el.clientHeight + 1;
        if (canScrollY) {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}

/**
 * @param section The tall track whose length defines scroll range (document case)
 *   or that lives inside `scrollRoot` (nested scrollport case).
 * @param scrollRoot Nearest `overflow-y: auto|scroll` ancestor of `section`, if any.
 *   When set, progress is `scrollRoot.scrollTop / maxScroll` so **only** that
 *   element’s scroll drives phases (page/window scroll outside it does not).
 */
export function computeDocumentSectionScrollProgress(
    section: HTMLElement,
    scrollRoot?: HTMLElement | null
): number {
    if (scrollRoot) {
        const maxScroll = Math.max(
            0,
            scrollRoot.scrollHeight - scrollRoot.clientHeight
        );
        if (maxScroll <= 0) {
            return 0;
        }
        return clamp01(scrollRoot.scrollTop / maxScroll);
    }

    const trackH = section.offsetHeight;
    const viewH = window.innerHeight;
    const scrollable = trackH - viewH;
    const rect = section.getBoundingClientRect();

    if (scrollable <= 0) {
        if (rect.bottom <= 0) {
            return 1;
        }
        if (rect.top >= viewH) {
            return 0;
        }
        const past = Math.min(Math.max(-rect.top, 0), trackH);
        return trackH <= 0 ? 0 : clamp01(past / trackH);
    }

    const scrolled = Math.min(Math.max(-rect.top, 0), scrollable);
    return clamp01(scrolled / scrollable);
}

export function useSpiderwebDocumentSectionProgress(
    sectionRef: RefObject<HTMLElement | null> | undefined,
    enabled: boolean
): { progress: number; isAttached: boolean } {
    const [progress, setProgress] = useState(0);
    const [isAttached, setIsAttached] = useState(false);
    const isAttachedRef = useRef(false);

    useLayoutEffect(() => {
        if (!enabled || !sectionRef) {
            setProgress(0);
            if (isAttachedRef.current) {
                isAttachedRef.current = false;
                setIsAttached(false);
            }
            return;
        }

        let detach: (() => void) | undefined;
        let pollRafId = 0;
        let scrollFlushRafId = 0;
        let cancelled = false;

        const attach = (target: HTMLElement) => {
            const scrollContainer = getNearestVerticalScrollContainer(target);

            const flushProgress = () => {
                if (!cancelled) {
                    setProgress(
                        computeDocumentSectionScrollProgress(
                            target,
                            scrollContainer
                        )
                    );
                }
            };

            const scheduleScrollFlush = () => {
                if (scrollFlushRafId !== 0) {
                    return;
                }
                scrollFlushRafId = requestAnimationFrame(() => {
                    scrollFlushRafId = 0;
                    flushProgress();
                });
            };

            flushProgress();
            isAttachedRef.current = true;
            setIsAttached(true);

            if (scrollContainer) {
                scrollContainer.addEventListener(
                    "scroll",
                    scheduleScrollFlush,
                    {
                        passive: true
                    }
                );
            } else {
                window.addEventListener("scroll", scheduleScrollFlush, {
                    passive: true
                });
            }
            window.addEventListener("resize", flushProgress);
            const ro = new ResizeObserver(flushProgress);
            ro.observe(target);
            if (scrollContainer) {
                ro.observe(scrollContainer);
            }

            detach = () => {
                cancelAnimationFrame(scrollFlushRafId);
                scrollFlushRafId = 0;
                if (scrollContainer) {
                    scrollContainer.removeEventListener(
                        "scroll",
                        scheduleScrollFlush
                    );
                } else {
                    window.removeEventListener("scroll", scheduleScrollFlush);
                }
                window.removeEventListener("resize", flushProgress);
                ro.disconnect();
                if (!cancelled) {
                    isAttachedRef.current = false;
                    setIsAttached(false);
                    setProgress(0);
                }
            };
        };

        const el = sectionRef.current;
        if (el) {
            attach(el);
            return () => {
                cancelled = true;
                detach?.();
            };
        }

        const poll = () => {
            if (cancelled) {
                return;
            }
            const node = sectionRef.current;
            if (node) {
                attach(node);
                return;
            }
            pollRafId = requestAnimationFrame(poll);
        };
        pollRafId = requestAnimationFrame(poll);

        return () => {
            cancelled = true;
            cancelAnimationFrame(pollRafId);
            detach?.();
        };
    }, [enabled, sectionRef]);

    return { progress, isAttached };
}
